import type { ReactElement } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/app/providers/auth-context'
import { AuthLoadingScreen } from '@/components/AuthLoadingScreen'

type RequireAuthProps = {
  children: ReactElement
  requireRole?: 'super_admin' | 'club_staff'
  requireOrgId?: string
}

export function RequireAuth({ children, requireRole, requireOrgId }: RequireAuthProps) {
  const { state, isSuperAdmin, isClubStaff, isAnyClubStaff } = useAuth()
  const location = useLocation()

  if (state === 'checking') {
    // Session is resolving (silent refresh / profile load). Reuse the shared
    // auth-transition ring so the post-login path is one continuous loader —
    // ring on /auth/callback → ring here → the destination page's own
    // skeleton — instead of a mismatched card floating in the middle.
    return <AuthLoadingScreen label="Checking your session…" />
  }

  if (state !== 'authenticated') {
    const returnTo = `${location.pathname}${location.search}`
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />
  }

  // Super admin bypass — they have access to every club-staff
  // route regardless of org membership. Placed before the per-role
  // checks so a transient empty organizations[] (e.g. /organizations
  // hiccuped on cold load) cannot block a known super admin.
  const superAdmin = isSuperAdmin()
  if (superAdmin) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[RequireAuth]', {
        path: location.pathname,
        requireRole,
        requireOrgId,
        bypass: 'super_admin',
      })
    }
    return children
  }

  if (requireRole === 'super_admin') {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[RequireAuth]', { path: location.pathname, requireRole, deny: 'not super_admin' })
    }
    return <Navigate to="/forbidden" replace />
  }

  if (requireRole === 'club_staff') {
    // Route-level gate: when no specific orgId is pinned to the
    // route, accept if the user is club staff of ANY org and let
    // the page itself enforce per-resource access once it knows
    // the owning orgId. Hard refresh on /tournaments/:id/edit used
    // to fail because isClubStaff() falls back to currentOrgId =
    // organizations[0], which is not necessarily the tournament's
    // owning org — a "real" club admin would land on /forbidden.
    const ok = requireOrgId ? isClubStaff(requireOrgId) : isAnyClubStaff()
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[RequireAuth]', {
        path: location.pathname,
        requireRole,
        requireOrgId,
        ok,
        decision: ok ? 'allow' : 'deny',
      })
    }
    if (!ok) return <Navigate to="/forbidden" replace />
  }

  if (requireOrgId && !isClubStaff(requireOrgId)) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[RequireAuth]', {
        path: location.pathname,
        requireOrgId,
        deny: 'not club_staff of pinned org',
      })
    }
    return <Navigate to="/forbidden" replace />
  }

  return children
}
